/**
 * Field-edit configuration for the agent HITL confirmation modal.
 *
 * Maps each proposed write-tool field to how it should be edited inline: free
 * text, a single select, a multi-select (with its option set), or read-only.
 * Option sources are reused from the existing incident/ICS forms — this file
 * does NOT define new option lists except for the two status enums that live
 * only in the backend tool schemas.
 *
 * It also knows where each tool nests its fields in the tool args (so an edit
 * lands in the right place): the generic create_ics_record / update_ics_record
 * tools nest entity fields under `data`; update_incident_info keeps them
 * top-level. `edited_args` fully replaces the tool args on resume, so
 * buildEditedArgs() merges edits onto a copy of the original args.
 */

import {
  USCGResourceKindOptions,
  TypeOptions,
} from 'components/IncidentConfig/ResourceTypes';
import { incidentObjectiveTypeSegments } from 'components/IAP/incidentObjectiveTypes';
import { COMS_TYPES } from 'components/IAP/ICSConstants';
import {
  PHYSICAL_HAZARDS,
  ENVIRONMENTAL_HAZARDS,
  OTHER_HAZARDS,
  POTENTIAL_HAZARDS,
  REQUIRED_PPE,
  HAZMAT_CLASSIFICATION,
  INCIDENT_NEEDS,
} from 'components/IAP/initialResponsePage/SafetyAnalysis/types';

export type EditKind =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multiselect'
  /** Plain object edited as labeled per-key rows (e.g. weather). */
  | 'object'
  | 'readonly';

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldConfig {
  editKind: EditKind;
  /**
   * Human-readable label shown in the confirmation UI (falls back to a
   * humanized field key).
   */
  label?: string;
  /** Static option set for select / multiselect. */
  options?: SelectOption[];
  /**
   * Marks a field whose options must be resolved at render time from a hook
   * (id→name lists keyed here): AORs, incident objectives, resources, incident
   * roster members ('poc'), or organization members ('member').
   * ReviewQueueModal supplies the resolved option sets. 'poc' is
   * incident-roster-only to match the UI's POC pickers (PRAT-5710); 'member' is
   * the wider org∪incident union for tools that may reference users not yet on
   * the incident (e.g. assign_roster_position, which adds them).
   */
  dynamicOptions?: 'aor' | 'objective' | 'resource' | 'poc' | 'member';
}

const stringsToOptions = (values: readonly string[]): SelectOption[] =>
  values.map((v) => ({ value: v, label: v }));

// Option sets reused from existing forms.
const OBJECTIVE_TYPE_OPTIONS: SelectOption[] =
  incidentObjectiveTypeSegments.map((s) => ({
    value: s.value,
    label: s.tooltip,
  }));

// Status enums live only in the backend write-tool schemas (entityDescriptors.ts); mirror them here so an edit
// validates server-side. Keep in sync with COMS ACTION_STATUSES / RESOURCE_STATUSES.
const ACTION_STATUS_OPTIONS = stringsToOptions([
  'Planned',
  'Current/Ongoing',
  'Closed',
]);
const RESOURCE_STATUS_OPTIONS = stringsToOptions([
  'Onsite',
  'Ordered',
  'In Transit',
  'Deployed',
]);

/** Tools whose entity fields are nested under args.data (vs. top-level). */
const DATA_NESTED_TOOLS = new Set(['create_ics_record', 'update_ics_record']);

export const isDataNestedTool = (toolName: string): boolean =>
  DATA_NESTED_TOOLS.has(toolName);

/**
 * Multi-field single-record proposals reviewed one field at a time (each diff
 * field becomes its own card in the review queue). Record-per-call entities
 * (objectives, actions, resources, roster positions) already arrive as one
 * confirmation per record and stay whole.
 */
const SPLIT_ENTITIES = new Set(['safety_message', 'hazmat']);

export const isSplitConfirmation = (
  toolName: string,
  entity: string | undefined
): boolean =>
  toolName === 'update_incident_info' ||
  (isDataNestedTool(toolName) && !!entity && SPLIT_ENTITIES.has(entity));

/**
 * Fields the backend Zod schema requires on create — rejecting one of these
 * cards rejects the WHOLE create (the remaining fields would fail validation on
 * resume without it), rather than dropping just that field.
 */
const REQUIRED_ON_CREATE: Record<string, Set<string>> = {
  safety_message: new Set(['safetyMessage']),
};

export const isRequiredOnCreate = (
  toolName: string,
  entity: string | undefined,
  field: string
): boolean =>
  toolName === 'create_ics_record' &&
  !!entity &&
  (REQUIRED_ON_CREATE[entity]?.has(field) ?? false);

/**
 * Per-entity field configs for the generic ICS record tools, keyed by entity
 * then field.
 */
const ICS_RECORD_FIELDS: Record<string, Record<string, FieldConfig>> = {
  objective: {
    name: { editKind: 'text', label: 'Name' },
    description: { editKind: 'textarea', label: 'Description' },
    type: {
      editKind: 'select',
      label: 'Type',
      options: OBJECTIVE_TYPE_OPTIONS,
    },
  },
  action: {
    name: { editKind: 'text', label: 'Name' },
    // description is DEPRECATED for actions (PRAT-5710): the action UI has no
    // entry field for it and the backend ignores it on write, so it must not
    // be reviewable/editable here. (The backend also strips it from preview
    // diffs, so no card should ever carry it.)
    status: {
      editKind: 'select',
      label: 'Status',
      options: ACTION_STATUS_OPTIONS,
    },
    // Show the linked objective / resources / POC by NAME (options resolved at
    // render time); the select still stores the underlying id for resume.
    objective_id: {
      editKind: 'select',
      label: 'Objective',
      dynamicOptions: 'objective',
    },
    resources: {
      editKind: 'multiselect',
      label: 'Resources',
      dynamicOptions: 'resource',
    },
    poc: {
      editKind: 'select',
      label: 'Point of Contact',
      dynamicOptions: 'poc',
    },
    start_date: { editKind: 'text', label: 'Start Date' },
    target_date: { editKind: 'text', label: 'Target Date' },
  },
  resource: {
    name: { editKind: 'text', label: 'Name' },
    kind: {
      editKind: 'select',
      label: 'Kind',
      options: USCGResourceKindOptions,
    },
    type: { editKind: 'select', label: 'Type', options: TypeOptions },
    quantity: { editKind: 'text', label: 'Quantity' },
    status: {
      editKind: 'select',
      label: 'Status',
      options: RESOURCE_STATUS_OPTIONS,
    },
    description: { editKind: 'textarea', label: 'Description' },
    eta: { editKind: 'text', label: 'ETA' },
    date_time_ordered: { editKind: 'text', label: 'Date/Time Ordered' },
  },
  // Roster (ICS 201 §6). position_key is constrained server-side to the canonical ICS position set; kept as a labeled
  // text field here so DICE doesn't duplicate/drift that list.
  org_assignment: {
    position_key: { editKind: 'text', label: 'Position' },
    name: { editKind: 'text', label: 'Assigned To' },
  },
  // Safety message (ICS 201 §8).
  safety_message: {
    safetyMessage: { editKind: 'textarea', label: 'Safety Message' },
    safetyOfficer: { editKind: 'text', label: 'Safety Officer' },
    dutyPhone: { editKind: 'text', label: 'Duty Phone' },
    email: { editKind: 'text', label: 'Email' },
    sopAndSafeWorkPractices: {
      editKind: 'textarea',
      label: 'SOP & Safe Work Practices',
    },
    comments: { editKind: 'textarea', label: 'Comments' },
    datetimeInitiated: { editKind: 'text', label: 'Initiated' },
    // ICS 208 structured blocks (jsonb) — edited per key like weather. The backend re-splits comma-joined
    // text edits back into arrays and normalizes hazard/PPE values onto the canonical vocabulary.
    knownHazards: { editKind: 'object', label: 'Known Hazards' },
    requiredPpe: { editKind: 'object', label: 'Required PPE' },
    healthAndSafetyInfo: { editKind: 'object', label: 'Health & Safety Plan' },
  },
  // HAZMAT (ICS 201 §8 / ICS 208HM). material[] is a complex array — left to the readonly fallback.
  hazmat: {
    classifications: {
      editKind: 'multiselect',
      label: 'Classifications',
      options: stringsToOptions(HAZMAT_CLASSIFICATION),
    },
    incidentNeeds: {
      editKind: 'multiselect',
      label: 'Incident Needs',
      options: stringsToOptions(INCIDENT_NEEDS),
    },
    otherClassification: { editKind: 'text', label: 'Other Classification' },
    sops: { editKind: 'textarea', label: 'SOPs' },
    decontaminationProcedure: {
      editKind: 'textarea',
      label: 'Decontamination Procedure',
    },
    emergencyProcedures: {
      editKind: 'textarea',
      label: 'Emergency Procedures',
    },
    additionalProductInfo: {
      editKind: 'textarea',
      label: 'Additional Product Info',
    },
    hazardMonitoringComments: {
      editKind: 'textarea',
      label: 'Hazard Monitoring Comments',
    },
  },
  // ICS 206 medical plan — labels mirror the medical-plan table headers
  // (MedicalPlan/HospitalsTable, MedicalAidStationsTable, TransportationsTable).
  // The capability flags are readonly for the same reason as assign_roster_position.is_leader: the backend schema
  // requires a boolean, and an edited free-text value would fail zod on resume. Reject the card to drop the record.
  hospital: {
    hospital_name: { editKind: 'text', label: 'Hospital' },
    address: { editKind: 'text', label: 'Address' },
    contact_number: { editKind: 'text', label: 'Contact' },
    travel_time_air: { editKind: 'text', label: 'Travel (Air)' },
    travel_time_ground: { editKind: 'text', label: 'Travel (Ground)' },
    trauma_center: { editKind: 'readonly', label: 'Trauma Center' },
    trauma_center_level: { editKind: 'text', label: 'Trauma Level' },
    burn_center: { editKind: 'readonly', label: 'Burn Center' },
    heli_pad: { editKind: 'readonly', label: 'Heli Pad' },
  },
  medical_aid_station: {
    name: { editKind: 'text', label: 'Name' },
    location: { editKind: 'text', label: 'Location' },
    contact_number: { editKind: 'text', label: 'Contact' },
    paramedics_on_site: { editKind: 'readonly', label: 'Paramedics On-Site' },
  },
  medical_transportation: {
    ambulance_service: { editKind: 'text', label: 'Ambulance Service' },
    address: { editKind: 'text', label: 'Address' },
    contact_number: { editKind: 'text', label: 'Contact' },
    paramedics_on_board: { editKind: 'readonly', label: 'Paramedics On Board' },
    als: { editKind: 'readonly', label: 'ALS' },
    bls: { editKind: 'readonly', label: 'BLS' },
  },
  // ICS 205 radio communications plan — labels mirror the IncidentComsTable
  // column headers. `type` mirrors COMS_TYPES; the work group is addressed by
  // name (resolved server-side against the incident roster).
  communication: {
    type: { editKind: 'select', label: 'Type', options: COMS_TYPES },
    channel_number: { editKind: 'text', label: 'Channel #' },
    function: { editKind: 'text', label: 'Function' },
    channel_name: { editKind: 'text', label: 'Channel Name / Talk Group' },
    work_group_name: { editKind: 'text', label: 'Assignment (Work Group)' },
    work_group_id: {
      editKind: 'readonly',
      label: 'Assignment (Work Group Id)',
    },
    rx_frequency: { editKind: 'text', label: 'RX Freq N or W' },
    rx_tone_nac: { editKind: 'text', label: 'RX Tone/NAC' },
    tx_frequency: { editKind: 'text', label: 'TX Freq N or W' },
    tx_tone_nac: { editKind: 'text', label: 'TX Tone/NAC' },
    mode: { editKind: 'text', label: 'Mode (A, D, M)' },
    remarks: { editKind: 'textarea', label: 'Remarks' },
    cell_phone_number: { editKind: 'text', label: 'Cell Phone' },
    other: { editKind: 'text', label: 'Other' },
  },
  // ICS-204 assignment list card (labels mirror the 204 card's blocks).
  assignment_list: {
    work_group_name: { editKind: 'text', label: 'Work Group' },
    branch: { editKind: 'text', label: 'Branch' },
    division: { editKind: 'text', label: 'Division' },
    group: { editKind: 'text', label: 'Group' },
    staging_area: { editKind: 'text', label: 'Staging Area' },
    special_instructions: {
      editKind: 'textarea',
      label: 'Special Instructions',
    },
    medical_communication: { editKind: 'textarea', label: 'Medical' },
    evacuation_communication: { editKind: 'textarea', label: 'Evacuation' },
    other_communication: { editKind: 'textarea', label: 'Other' },
  },
};

// Keys are camelCase to match the backend update_incident_info tool input
// (UpdateIncidentInfoTool schema), NOT the snake_case DB columns.
const UPDATE_INCIDENT_INFO_FIELDS: Record<string, FieldConfig> = {
  situationSummary: { editKind: 'textarea', label: 'Situation Summary' },
  priorities: { editKind: 'textarea', label: 'Priorities' },
  operationalPeriodCommandEmphasis: {
    editKind: 'textarea',
    label: 'Command Emphasis',
  },
  incidentLocation: { editKind: 'text', label: 'Incident Location' },
  keyDecisionsAndProcedures: {
    editKind: 'textarea',
    label: 'Key Decisions & Procedures',
  },
  // ICS 206 §7 narrative (the text area under the medical-plan tables).
  specialMedicalEmergencyProcedures: {
    editKind: 'textarea',
    label: 'Special Medical Emergency Procedures',
  },
  // ICS 205 narrative (the text area under the radio channel table).
  communicationsSpecialInstructions: {
    editKind: 'textarea',
    label: 'Special Instructions',
  },
  // ICS 201 §8 Safety Analysis fields (text[] columns; the agent sets these via update_incident_info).
  physicalHazards: {
    editKind: 'multiselect',
    label: 'Physical Hazards',
    options: stringsToOptions(PHYSICAL_HAZARDS),
  },
  environmentalHazards: {
    editKind: 'multiselect',
    label: 'Environmental Hazards',
    options: stringsToOptions(ENVIRONMENTAL_HAZARDS),
  },
  otherHazards: {
    editKind: 'multiselect',
    label: 'Other Hazards',
    options: stringsToOptions(OTHER_HAZARDS),
  },
  potentialHazards: {
    editKind: 'multiselect',
    label: 'Potential Hazards',
    options: stringsToOptions(POTENTIAL_HAZARDS),
  },
  requiredPpe: {
    editKind: 'multiselect',
    label: 'Required PPE',
    options: stringsToOptions(REQUIRED_PPE),
  },
  notesOnPpe: { editKind: 'textarea', label: 'Notes on PPE' },
  // weather is a structured object (wind/tides/forecast/…) — edited per key.
  weather: { editKind: 'object', label: 'Weather' },
};

// Bespoke (non-entity) write tools with flat top-level args, keyed by tool
// name then field. assign_roster_position: user_guid resolves to a member name
// via the org-wide member option set (raw GUIDs must never face the reviewer,
// and the assignee may not be on the incident yet — this tool adds them); is_leader
// is readonly because the backend schema requires a boolean — an edited text
// string would fail zod on resume.
const BESPOKE_TOOL_FIELDS: Record<string, Record<string, FieldConfig>> = {
  assign_roster_position: {
    user_guid: {
      editKind: 'select',
      label: 'Member',
      dynamicOptions: 'member',
    },
    position_name: { editKind: 'text', label: 'Position' },
    is_leader: { editKind: 'readonly', label: 'Team Leader' },
  },
  // The member being removed already holds the seat, so the incident-roster
  // ('poc') option set always resolves them.
  remove_roster_position: {
    user_guid: { editKind: 'select', label: 'Member', dynamicOptions: 'poc' },
    position_name: { editKind: 'text', label: 'Position' },
  },
};

/** The registry lookup shared by getFieldConfig and getFieldLabel. */
function explicitFieldConfig(
  toolName: string,
  entity: string | undefined,
  field: string
): FieldConfig | undefined {
  if (isDataNestedTool(toolName) && entity) {
    return ICS_RECORD_FIELDS[entity]?.[field];
  }
  if (toolName === 'update_incident_info') {
    return UPDATE_INCIDENT_INFO_FIELDS[field];
  }
  return BESPOKE_TOOL_FIELDS[toolName]?.[field];
}

/**
 * Label for a value in an option list, if resolvable — used to display
 * select/multiselect values (e.g. a user GUID) by their human label. Returns
 * undefined on a miss so callers can fall back to their own formatting.
 */
export const optionLabel = (
  value: unknown,
  options: SelectOption[]
): string | undefined =>
  value === null || value === undefined
    ? undefined
    : options.find((o) => o.value === String(value))?.label;

const isPrimitive = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  typeof v === 'string' ||
  typeof v === 'number' ||
  typeof v === 'boolean';

export const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Resolve how a given tool/entity/field should be edited. Fields not in the
 * registry fall back by value shape: primitives → free text, plain objects →
 * labeled per-key rows, anything else (arrays of objects, etc.) → read-only —
 * so unknown complex structured data is never silently editable.
 */
export function getFieldConfig(
  toolName: string,
  entity: string | undefined,
  field: string,
  value: unknown
): FieldConfig {
  const explicit = explicitFieldConfig(toolName, entity, field);
  if (explicit) return explicit;
  if (isPrimitive(value)) return { editKind: 'text' };
  if (isPlainObject(value)) return { editKind: 'object' };
  return { editKind: 'readonly' };
}

/** Short tokens that read as acronyms rather than words when humanizing keys. */
const ACRONYMS = new Set([
  'id',
  'eta',
  'ppe',
  'sop',
  'url',
  'gps',
  'ics',
  'iap',
  'neo',
  'gpt',
]);

/**
 * Humanize a raw field key into a Title Case label. Handles both camelCase
 * (`situationSummary`) and snake_case (`situation_summary`) → "Situation
 * Summary". Used as the fallback when a field has no configured `label`.
 */
export function humanizeFieldName(field: string): string {
  const words = field
    // split snake_case / kebab-case
    .replace(/[_-]+/g, ' ')
    // split camelCase / PascalCase boundaries (incl. ACRONYMFollowedByWord)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((w) =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
}

/**
 * Resolve the human-readable label for a tool/entity/field. Prefers the
 * configured `label`; otherwise humanizes the raw key so the confirmation UI
 * never shows a snake_case / camelCase database name.
 */
export function getFieldLabel(
  toolName: string,
  entity: string | undefined,
  field: string
): string {
  return (
    explicitFieldConfig(toolName, entity, field)?.label ??
    humanizeFieldName(field)
  );
}

/** Read a field's value from the tool args, honoring the tool's nesting. */
export function getArgValue(
  args: Record<string, unknown>,
  toolName: string,
  field: string
): unknown {
  if (isDataNestedTool(toolName)) {
    const data = (args?.data as Record<string, unknown>) ?? {};
    return data[field];
  }
  return args?.[field];
}

/**
 * Build the `edited_args` payload for a resume 'edit' decision: a copy of the
 * original args with the user's field edits merged in at the correct path.
 */
export function buildEditedArgs(
  args: Record<string, unknown>,
  toolName: string,
  edits: Record<string, unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(args ?? {}) };
  if (isDataNestedTool(toolName)) {
    next.data = {
      ...((args?.data as Record<string, unknown>) ?? {}),
      ...edits,
    };
  } else {
    Object.assign(next, edits);
  }
  return next;
}

/**
 * Build `edited_args` for a split confirmation with mixed per-field verdicts:
 * rejected fields are removed from the copied args (update tools only write the
 * fields present, so an omitted field is simply left untouched), then the
 * accepted fields' edits are merged in. Envelope keys (entity, record ids) live
 * outside the field set and are never dropped.
 */
export function buildEditedArgsPartial(
  args: Record<string, unknown>,
  toolName: string,
  edits: Record<string, unknown>,
  omitFields: string[]
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(args ?? {}) };
  if (isDataNestedTool(toolName)) {
    const data = { ...((args?.data as Record<string, unknown>) ?? {}) };
    for (const field of omitFields) delete data[field];
    next.data = { ...data, ...edits };
  } else {
    for (const field of omitFields) delete next[field];
    Object.assign(next, edits);
  }
  return next;
}
